import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { Loader2, X } from "lucide-react";
import { formatDrawCoin } from "../../utils/bondDrawHelpers";

const BondDrawCashoutModal = ({
  open = false,
  maxAmount = 0,
  loading = false,
  onClose,
  onSubmit,
}) => {
  const [amount, setAmount] = useState("");

  useEffect(() => {
    if (!open) setAmount("");
  }, [open]);

  const parsed = useMemo(() => Math.floor(Number(amount || 0)), [amount]);
  const max = Math.max(0, Math.floor(Number(maxAmount || 0)));
  const clamped = useMemo(() => {
    if (!Number.isFinite(parsed) || parsed <= 0) return 0;
    return Math.min(parsed, max);
  }, [parsed, max]);

  const quickPicks = useMemo(() => {
    if (max <= 0) return [];
    const step = max >= 5000 ? 1000 : max >= 2000 ? 500 : 100;
    const round = (x) => Math.max(step, Math.round(x / step) * step);
    const candidates = [0.1, 0.25, 0.5, 0.75]
      .map((p) => round(max * p))
      .filter((v) => v > 0 && v < max);
    return Array.from(new Set(candidates)).sort((a, b) => a - b).slice(0, 4);
  }, [max]);

  if (!open) return null;

  const disabled = loading || !Number.isFinite(clamped) || clamped <= 0;

  return createPortal(
    <div className="bdraw-cashout-root" role="dialog" aria-modal="true" aria-labelledby="bdraw-cashout-title">
      <button type="button" className="bdraw-cashout-backdrop" aria-label="Close" disabled={loading} onClick={onClose} />
      <div className="bdraw-cashout-sheet">
        <header className="bdraw-cashout-head">
          <div>
            <p className="bdraw-cashout-kicker">Investor pool</p>
            <h2 id="bdraw-cashout-title" className="bdraw-cashout-title">Cash Out</h2>
            <p className="bdraw-cashout-subtitle">Available: {formatDrawCoin(max)} COIN</p>
          </div>
          <button type="button" className="bdraw-cashout-close" aria-label="Close" disabled={loading} onClick={onClose}>
            <X size={16} strokeWidth={2.5} />
          </button>
        </header>

        <label className="bdraw-cashout-label" htmlFor="bdraw-cashout-amount">
          Amount (COIN)
        </label>
        <input
          id="bdraw-cashout-amount"
          className="bdraw-cashout-input"
          type="number"
          min="1"
          max={max || undefined}
          placeholder="e.g. 5000"
          value={amount}
          disabled={loading}
          onChange={(e) => setAmount(e.target.value)}
        />

        {quickPicks.length > 0 ? (
          <div className="bdraw-cashout-quick">
            {quickPicks.map((value) => (
              <button
                key={value}
                type="button"
                className="bdraw-cashout-quick-btn"
                disabled={loading}
                onClick={() => setAmount(String(value))}
              >
                {formatDrawCoin(value)}
              </button>
            ))}
            <button
              type="button"
              className="bdraw-cashout-quick-btn all"
              disabled={loading}
              onClick={() => setAmount(String(max))}
            >
              Max
            </button>
          </div>
        ) : null}

        <div className="bdraw-cashout-actions">
          <button type="button" className="bdraw-cashout-btn secondary" disabled={loading} onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="bdraw-cashout-btn primary"
            disabled={disabled}
            onClick={() => onSubmit(clamped)}
          >
            {loading ? (
              <>
                <Loader2 size={14} className="bdraw-spin" />
                Withdrawing…
              </>
            ) : (
              "Withdraw"
            )}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default BondDrawCashoutModal;
